import { useCallback, useMemo, useRef, type RefObject } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

import { useReducedMotion } from "@/shared/motion";

/**
 * 机器人状态。notify 不在此列 —— 它是**一次性事件**而非状态:做成 mode 会让球在红点亮着期间
 * 一直卡在 notify 态、呼吸停掉变成僵尸球。见 RobotMotion.notify()。
 */
export type RobotMode = "idle" | "hover" | "drag";

export interface RobotMotion {
  /** 拖动中按横向速度倾斜(每帧调用,走 quickTo 不进 React)。 */
  tilt: (vx: number) => void;
  /** 眼睛朝光标方向偏移;dx/dy = 光标相对球心的偏移(px)。 */
  lookAt: (dx: number, dy: number) => void;
  /** 眼睛回正。 */
  lookAway: () => void;
  /** 有新作品时跳一下 + 摇头(一次性)。 */
  notify: () => void;
}

/** 眼睛最大偏移(SVG 用户坐标) —— 再多就斗鸡眼了。 */
const LOOK_RADIUS = 1.6;
/** 倾斜上限(deg)。 */
const TILT_MAX = 14;

interface Imperative {
  tilt: (vx: number) => void;
  lookAt: (dx: number, dy: number) => void;
  lookAway: () => void;
}
const NOOP_IMP: Imperative = { tilt: () => {}, lookAt: () => {}, lookAway: () => {} };

/**
 * 机器人状态动效。挂在球根元素上,用 data-part 选择器驱动 SVG 各部件。
 *
 * <p>★元素分层(**一个元素的 transform 只能有一个主人**):
 * <ul>
 *   <li>root(button):**只有**停靠位移 x/y(useLauncherDock 写);
 *   <li>rig(span):入场 scale/opacity;
 *   <li>svg:呼吸 scale、hover 抬头 y、drag 倾斜 rotation、notify 跳动;
 *       (旋转一律用 GSAP 规范名 `rotation`,不是 `rotate` —— 后者与现代 CSS 的独立 rotate 属性撞名,
 *        revert 时 GSAP 会告警 "rotate not eligible for reset" 且清不干净。)
 *   <li>eyes:眨眼 scaleY、注视 x/y;glow:灯脉冲 opacity。
 * </ul>
 *
 * <p>为什么入场要单独一层 rig,而不是直接 scale root:x/y 和 scale 会合成**同一个** transform
 * 字符串。入场建于 layout effect(早于 dock 落位的 passive effect),tween 每帧按自己建时缓存的
 * x=0,y=0 重写 transform → 把停靠位覆写成 translate(0,0),球卡在屏幕左上角。
 * 这个 bug 真在浏览器里发生过(单测无布局抓不到),分层是它的根治。
 *
 * <p>★所有动画都必须在 useGSAP 回调的**同步执行栈内**创建,或经 contextSafe 包裹 —— gsap.context
 * 只收编同步期创建的动画,延迟回调里新建的会逃出 revert 变永动机(见 notify / 眨眼的注释)。
 * 另需全局 `gsap.registerPlugin(useGSAP)`(见 shared/motion/registerGsap),否则 context 与
 * 组件里的 gsap 不是同一实例,revert 收不到任何东西。
 *
 * <p>硬约束:①只动 transform/opacity(不触发重排);②prefers-reduced-motion 全跳过;
 * ③克制 —— idle 幅度 ≤3.5%,notify 一次性不循环(循环的通知动效就是骚扰);
 * ④tab 不可见时暂停(后台标签页不空转)。
 */
export function useRobotMotion(scope: RefObject<HTMLElement>, mode: RobotMode): RobotMotion {
  const reduced = useReducedMotion();
  const reducedRef = useRef(reduced);
  reducedRef.current = reduced;
  const imp = useRef<Imperative>(NOOP_IMP);
  const notifyTl = useRef<gsap.core.Timeline | null>(null);

  // —— 入场:deps [] → 只建一次,不被 mode 变化(hover 等)重放 ——
  const { contextSafe } = useGSAP(
    () => {
      if (reducedRef.current || !scope.current) return;
      // 入场只碰 rig 层(见上方分层说明:root 的 transform 归停靠位独占,碰不得)。
      gsap.from(gsap.utils.selector(scope)('[data-part="rig"]'), {
        opacity: 0,
        scale: 0.4,
        duration: 0.5,
        ease: "back.out(1.7)",
      });
    },
    { scope, dependencies: [] }
  );

  // notify 由事件触发(在 useGSAP 同步栈之外)→ 必须 contextSafe 包裹,否则这条 timeline 不受
  // cleanup 管辖,卸载后仍会跑完。contextSafe 绑的是上面 deps:[] 的 context(只在卸载时 revert),
  // 故 mode 变化不会打断正在跑的 notify。
  const notify = useMemo(
    () =>
      contextSafe(() => {
        if (reducedRef.current || !scope.current) return;
        const svg = gsap.utils.selector(scope)("svg");
        // 红点快速 false→true→false→true 时,旧 timeline 未跑完就叠新的会抽搐 → 先杀旧的归位。
        notifyTl.current?.kill();
        gsap.set(svg, { y: 0, rotation: 0 });
        notifyTl.current = gsap
          .timeline()
          .to(svg, { y: -8, duration: 0.22, ease: "power2.out" })
          .to(svg, { y: 0, duration: 0.5, ease: "bounce.out" })
          .to(svg, { rotation: 8, duration: 0.1 })
          .to(svg, { rotation: -8, duration: 0.12 })
          .to(svg, { rotation: 0, duration: 0.1 });
      }),
    [contextSafe, scope]
  );

  // —— 状态机:mode 变化时 revert 上一态再建新态 ——
  useGSAP(
    () => {
      if (reduced || !scope.current) {
        imp.current = NOOP_IMP;
        return;
      }
      const q = gsap.utils.selector(scope);
      const eyes = q('[data-part="eye-l"], [data-part="eye-r"]');
      const glow = q('[data-part="glow"]');
      const svg = q("svg");
      // 造型换了却漏掉某个 data-part 时,gsap 会对空目标刷 "GSAP target not found"、该动效静默消失。
      // 契约由 AdvisorRobot.test.tsx 锚定,这里再兜一层:宁可少一个动效,不刷控制台。
      if (!svg.length || !eyes.length) {
        imp.current = NOOP_IMP;
        return;
      }

      // 命令式接口:拖动/注视每帧调用,quickTo 避免每帧 setState
      const tiltTo = gsap.quickTo(svg, "rotation", { duration: 0.25, ease: "power2.out" });
      const eyeX = gsap.quickTo(eyes, "x", { duration: 0.3, ease: "power2.out" });
      const eyeY = gsap.quickTo(eyes, "y", { duration: 0.3, ease: "power2.out" });
      imp.current = {
        tilt: (vx) => tiltTo(gsap.utils.clamp(-TILT_MAX, TILT_MAX, vx * 0.35)),
        lookAt: (dx, dy) => {
          // 归一化到球尺度(±40px 视为满偏),再钳进 LOOK_RADIUS
          eyeX(gsap.utils.clamp(-LOOK_RADIUS, LOOK_RADIUS, (dx / 40) * LOOK_RADIUS));
          eyeY(gsap.utils.clamp(-LOOK_RADIUS, LOOK_RADIUS, (dy / 40) * LOOK_RADIUS));
        },
        lookAway: () => {
          eyeX(0);
          eyeY(0);
        },
      };

      const tl = gsap.timeline();

      if (mode === "idle" || mode === "hover") {
        // 呼吸:幅度压在 3.5% 以内(常驻元素,克制优先)
        tl.to(svg, { scale: 1.035, duration: 2.4, ease: "sine.inOut", yoyo: true, repeat: -1 }, 0);
        // 状态灯脉冲(opacity,不是尺寸)。standard 造型无天线,glow 是额前灯 —— 契约仍在。
        if (glow.length)
          tl.to(
            glow,
            { opacity: 0.45, duration: 1.2, ease: "sine.inOut", yoyo: true, repeat: -1 },
            0
          );
        // 随机间隔眨眼:**一条**自循环 timeline,不是递归 delayedCall —— 递归里新建的 delayedCall
        // 在 context 捕获窗口之外,永不被 revert;而 mode 每次 hover 都变,每变一次漏一条链,越用越多
        // (codex 红队 High #1 / 本地实测:卸载后 tween 仍在推进)。
        // repeatRefresh 让空 tween 的随机时长每轮重算 = 随机间隔。挂进 tl 以便一起暂停。
        tl.add(
          gsap
            .timeline({ repeat: -1, repeatRefresh: true })
            .to(eyes, { scaleY: 0.08, duration: 0.12, yoyo: true, repeat: 1, ease: "power2.inOut" })
            .to({}, { duration: () => gsap.utils.random(2.5, 6) }),
          0
        );
      }

      // hover 抬头示意「在听」
      if (mode === "hover") tl.to(svg, { y: -3, duration: 0.25, ease: "power2.out" }, 0);

      // drag 睁大眼(被拎起来的惊讶感);倾斜由 tilt() 每帧驱动
      if (mode === "drag")
        tl.to(eyes, { scaleY: 1.25, scaleX: 1.1, duration: 0.2, ease: "back.out(2)" }, 0);

      // 后台标签页不空转
      const onVis = () => (document.hidden ? tl.pause() : tl.resume());
      document.addEventListener("visibilitychange", onVis);
      return () => document.removeEventListener("visibilitychange", onVis);
    },
    { scope, dependencies: [mode, reduced], revertOnUpdate: true }
  );

  // 转发到 ref → 调用方拿到的引用恒稳定(不因 mode 变化重建回调链);对象本身 memo 化,
  // 免得消费侧以 motion 为依赖的 effect 每次渲染都重跑。
  const tilt = useCallback((vx: number) => imp.current.tilt(vx), []);
  const lookAt = useCallback((dx: number, dy: number) => imp.current.lookAt(dx, dy), []);
  const lookAway = useCallback(() => imp.current.lookAway(), []);
  return useMemo(() => ({ tilt, lookAt, lookAway, notify }), [tilt, lookAt, lookAway, notify]);
}
